import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Search } from "lucide-react";

/* Komponen tampilan bersama untuk halaman master (palet, rak). */

export function PageHeader({ title, subtitle, children }) {
    return (
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
                <h2 className="text-2xl font-semibold text-body tracking-tight">{title}</h2>
                {subtitle && <p className="text-sm text-muted mt-1">{subtitle}</p>}
            </div>
            {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
        </div>
    );
}

export function Button({ variant = "secondary", className = "", children, ...props }) {
    const styles = {
        primary: "bg-ink text-white hover:bg-ink-700 border border-ink",
        secondary: "bg-white text-body border border-line hover:border-ink",
        ghost: "text-ink hover:bg-ink-100 border border-transparent"
    };
    return (
        <button
            type="button"
            className={`inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${styles[variant]} ${className}`}
            {...props}
        >
            {children}
        </button>
    );
}

export function SearchInput({ value, onChange, placeholder, className = "" }) {
    return (
        <div className={`relative ${className}`}>
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
            <input
                type="text"
                inputMode="search"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="h-10 w-full pl-9 pr-9 text-sm bg-white border border-line rounded-md focus:border-ink focus:ring-0"
            />
            {value && (
                <button
                    type="button"
                    onClick={() => onChange("")}
                    aria-label="Hapus pencarian"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded text-muted hover:text-body hover:bg-surface"
                >
                    <X size={14} />
                </button>
            )}
        </div>
    );
}

export function Segmented({ value, onChange, options, label }) {
    return (
        <div className="h-10 inline-flex bg-white border border-line rounded-md p-1" role="radiogroup" aria-label={label}>
            {options.map((o) => {
                const active = value === o.value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => onChange(o.value)}
                        className={`px-3 text-sm rounded transition-colors whitespace-nowrap ${
                            active ? "bg-ink text-white font-semibold" : "text-muted hover:text-body"
                        }`}
                    >
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}

export function Field({ label, hint, error, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="block text-sm font-medium text-body mb-1">{label}</span>
            {children}
            {error ? (
                <span className="block text-xs text-red-600 mt-1">{error}</span>
            ) : hint ? (
                <span className="block text-xs text-muted mt-1">{hint}</span>
            ) : null}
        </label>
    );
}

export const inputClass = (error) =>
    `h-10 w-full bg-white border rounded-md px-3 text-sm text-body focus:ring-0 ${
        error ? "border-red-400 focus:border-red-500" : "border-line focus:border-ink"
    }`;

export function Modal({ open, title, subtitle, onClose, children, footer, size = "md" }) {
    const panelRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && onClose?.();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const widths = { sm: "max-w-md", md: "max-w-xl", lg: "max-w-3xl", xl: "max-w-5xl" };

    // dirender ke <body> supaya tidak terpengaruh margin/layout halaman
    return createPortal(
        <div
            className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
            onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                className={`bg-white rounded-xl shadow-2xl w-full ${widths[size]} max-h-[90vh] flex flex-col overflow-hidden`}
            >
                <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-line">
                    <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-body">{title}</h3>
                        {subtitle && <p className="text-sm text-muted mt-0.5">{subtitle}</p>}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Tutup"
                        className="p-2 -mr-2 rounded-md text-muted hover:text-body hover:bg-surface"
                    >
                        <X size={18} />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
                {footer && (
                    <div className="flex justify-end gap-2 px-6 py-4 border-t border-line bg-surface/50">{footer}</div>
                )}
            </div>
        </div>,
        document.body
    );
}

export function Pager({ pagination, onChange, unit = "data" }) {
    const page = pagination?.current_page || 1;
    const last = pagination?.last_page || 1;
    const total = pagination?.total || 0;
    const per = pagination?.per_page || 0;
    const from = total ? (page - 1) * per + 1 : 0;
    const to = Math.min(page * per, total);

    return (
        <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-muted">
                {from.toLocaleString("id-ID")}–{to.toLocaleString("id-ID")} dari {total.toLocaleString("id-ID")} {unit}
            </span>
            <div className="flex items-center gap-2">
                <span className="text-muted mr-1 hidden sm:inline">Halaman {page} dari {last}</span>
                <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => onChange(page - 1)}
                    className="h-8 px-3 border border-line rounded-md bg-white hover:bg-surface disabled:opacity-40 disabled:hover:bg-white"
                >
                    Sebelumnya
                </button>
                <button
                    type="button"
                    disabled={page >= last}
                    onClick={() => onChange(page + 1)}
                    className="h-8 px-3 border border-line rounded-md bg-white hover:bg-surface disabled:opacity-40 disabled:hover:bg-white"
                >
                    Berikutnya
                </button>
            </div>
        </div>
    );
}

// ambil pesan error dari response Laravel (string atau object validasi)
export const errorMessage = (err, fallback) => {
    const m = err?.message ?? err;
    if (m && typeof m === "object") return Object.values(m).flat().join("\n");
    return m || fallback;
};
